import { useState } from 'react'
import {
  PlaneTakeoff,
  PlaneLanding,
  Pencil,
  Trash2,
  Loader2,
  X,
  Check,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import type { FlightSegment } from './types'

interface EditableSegmentCardProps {
  segment: FlightSegment
  isEditing: boolean
  editData: Partial<FlightSegment>
  setEditData: (data: Partial<FlightSegment>) => void
  onStartEdit: () => void
  onCancelEdit: () => void
  onSave: () => void
  onDelete: () => void
  isSaving: boolean
  isDeleting: boolean
}

export function EditableSegmentCard({
  segment,
  isEditing,
  editData,
  setEditData,
  onStartEdit,
  onCancelEdit,
  onSave,
  onDelete,
  isSaving,
  isDeleting,
}: EditableSegmentCardProps) {
  const depTime = segment.dep_dt ? new Date(segment.dep_dt) : null
  const arrTime = segment.arr_dt ? new Date(segment.arr_dt) : null

  if (isEditing) {
    return (
      <div className="p-3 bg-slate-50 rounded-lg space-y-2">
        <div className="grid grid-cols-4 gap-2">
          <Input
            placeholder="Carrier"
            value={editData.carrier || ''}
            onChange={(e) => setEditData({ ...editData, carrier: e.target.value.toUpperCase() })}
            maxLength={2}
            className="font-mono uppercase"
          />
          <Input
            placeholder="Flugnr."
            value={editData.flight_no || ''}
            onChange={(e) => setEditData({ ...editData, flight_no: e.target.value })}
            className="font-mono"
          />
          <Input
            placeholder="DEP"
            value={editData.dep_iata || ''}
            onChange={(e) => setEditData({ ...editData, dep_iata: e.target.value.toUpperCase() })}
            maxLength={3}
            className="font-mono uppercase"
          />
          <Input
            placeholder="ARR"
            value={editData.arr_iata || ''}
            onChange={(e) => setEditData({ ...editData, arr_iata: e.target.value.toUpperCase() })}
            maxLength={3}
            className="font-mono uppercase"
          />
        </div>
        <div className="grid grid-cols-3 gap-2">
          <Input
            type="datetime-local"
            value={editData.dep_dt || ''}
            onChange={(e) => setEditData({ ...editData, dep_dt: e.target.value })}
          />
          <Input
            type="datetime-local"
            value={editData.arr_dt || ''}
            onChange={(e) => setEditData({ ...editData, arr_dt: e.target.value })}
          />
          <Input
            placeholder="Klasse"
            value={editData.booking_class || ''}
            onChange={(e) => setEditData({ ...editData, booking_class: e.target.value.toUpperCase() })}
            maxLength={2}
            className="font-mono uppercase"
          />
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onCancelEdit}>
            <X className="h-4 w-4" />
          </Button>
          <Button size="sm" onClick={onSave} disabled={isSaving}>
            {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg group">
      <div className="flex items-center gap-2 min-w-[80px]">
        <Badge variant="outline" className="font-mono text-xs">
          {segment.carrier}
          {segment.flight_no}
        </Badge>
      </div>
      <div className="flex items-center gap-2 flex-1">
        <div className="flex items-center gap-1">
          <PlaneTakeoff className="h-3.5 w-3.5 text-slate-400" />
          <span className="font-mono font-medium text-slate-900">{segment.dep_iata}</span>
          {depTime && (
            <span className="text-xs text-slate-500">
              {depTime.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
        </div>
        <span className="text-slate-300">→</span>
        <div className="flex items-center gap-1">
          <PlaneLanding className="h-3.5 w-3.5 text-slate-400" />
          <span className="font-mono font-medium text-slate-900">{segment.arr_iata}</span>
          {arrTime && (
            <span className="text-xs text-slate-500">
              {arrTime.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
        </div>
      </div>
      {segment.booking_class && (
        <Badge variant="secondary" className="text-xs">
          {segment.booking_class}
        </Badge>
      )}
      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onStartEdit}>
          <Pencil className="h-3 w-3" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-red-600 hover:text-red-700 hover:bg-red-50"
          onClick={onDelete}
          disabled={isDeleting}
        >
          {isDeleting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
        </Button>
      </div>
    </div>
  )
}
